"use client"

import { Suspense, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useAuth } from "@/lib/auth/auth-provider"
import { z } from "zod"
import { LoginFormSchema, EmailOnlySchema } from "@/lib/schemas/forms"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Button } from '@/components/animate-ui/components/buttons/button';
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Globe, Loader2, AlertCircle, Wind, Activity, MapPin, Settings2, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { GB, FR } from "@/components/shared/flag-icons"


// Not exported: a page module may only export the route segment API, and an
// extra named export fails Next's generated route typecheck during `next build`.
// Nothing outside this file uses it.
function LanguageBackDrop({
  language,
  setLanguage: setLanguageAction,
}: { language: string, setLanguage: (lang: string) => void; }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Globe />
          {language}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => setLanguageAction("English")}>
            <GB />
            English
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setLanguageAction("Français")}>
            <FR />
            Français
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function FeatureRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-3 text-left">
      <div className="flex items-center justify-center rounded-lg bg-white/15 p-2">
        {icon}
      </div>
      <span className="text-lg font-medium text-primary-foreground/90">{label}</span>
    </div>
  )
}

function LoginForm() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [passwordVisible, setPasswordVisible] = useState(false)

  const router = useRouter()
  const searchParams = useSearchParams()
  const { signIn } = useAuth()

  // Rules live in lib/schemas/forms.ts so the email check is defined once —
  // it used to be an inline regex duplicated here and in handleForgotPassword.
  // This form predates react-hook-form and keeps its own useState fields, so
  // it validates with safeParse rather than a resolver.
  const validateForm = () => {
    setEmailError("")
    setPasswordError("")
    setError("")

    const result = LoginFormSchema.safeParse({ email, password })
    if (result.success) return true

    const { fieldErrors } = z.flattenError(result.error)
    if (fieldErrors.email?.[0]) setEmailError(fieldErrors.email[0])
    if (fieldErrors.password?.[0]) setPasswordError(fieldErrors.password[0])
    return false
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setError("")

    try {
      await signIn(email, password)
      const next = searchParams.get("next") ?? "/dashboard"
      router.replace(next)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.")
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    if (!email) {
      toast.error("Please enter your email address first")
      return
    }

    const result = EmailOnlySchema.safeParse({ email })
    if (!result.success) {
      toast.error(z.flattenError(result.error).fieldErrors.email?.[0] ?? "Please enter a valid email address")
      return
    }

    toast.success("Password reset request has been sent")
  }

  return (
    <div className="mx-auto flex w-full max-w-[440px] flex-col gap-8">

      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground">Sign in to Your Account</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={e => {
              setEmail(e.target.value)
              if (emailError) setEmailError("")
            }}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "email-error" : undefined}
            disabled={isLoading}
          />
          {emailError && (
            <p id="email-error" className="text-sm text-destructive">{emailError}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password" className="text-sm font-semibold">
            Password
          </Label>
          <InputGroup>
            <InputGroupInput
              id="password"
              type={passwordVisible ? "text" : "password"}
              placeholder="Enter your Password"
              value={password}
              onChange={e => {
                setPassword(e.target.value)
                if (passwordError) setPasswordError("")
              }}
              aria-invalid={!!passwordError}
              aria-describedby={passwordError ? "password-error" : undefined}
              disabled={isLoading}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                aria-label={passwordVisible ? "Hide password" : "Show password"}
                aria-pressed={passwordVisible}
                onClick={() => setPasswordVisible(v => !v)}
                disabled={isLoading}
              >
                {passwordVisible ? <EyeOff /> : <Eye />}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          {passwordError && (
            <p id="password-error" className="text-sm text-destructive">{passwordError}</p>
          )}
        </div>

        <div className="flex items-center justify-start">
          <Button
            type="button"
            variant="link"
            className="px-0 font-normal text-sm"
            onClick={handleForgotPassword}
            disabled={!email}
          >
            Forget Password?
          </Button>
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" />
              Authenticating...
            </>
          ) : (
            "Login"
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Don&apos;t have an account? Request one from your manager
        </p>
      </form>
    </div>
  )
}

export default function LoginPage() {
  return (
    <div className="flex min-h-screen">
      <div className="relative flex w-full flex-col justify-center bg-background p-6 md:w-1/2">
        <Suspense>
          <LoginForm />
        </Suspense>
      </div>

      <div className="hidden w-1/2 p-4 md:block">
        <div className="relative size-full overflow-hidden rounded-[28px] bg-gradient-to-br from-primary to-primary/60 text-primary-foreground">
          {/* Decorative shapes */}
          <div className="absolute -top-10 -right-10 size-[200px] rounded-full bg-white/10" />
          <div className="absolute -bottom-16 -left-8 size-[260px] rounded-full bg-white/5" />
          <div className="absolute top-[120px] -left-[50px] size-[140px] rounded-full bg-white/5" />
          <div className="absolute bottom-20 -right-5 size-[120px] rotate-12 rounded-3xl bg-white/10" />

          {/* Content */}
          <div className="relative flex h-full flex-col items-center justify-center p-12 text-center">
            <Wind className="size-20 opacity-90" />
            <h2 className="mt-8 text-4xl font-bold">weditwin</h2>
            <p className="mt-4 max-w-md text-lg text-primary-foreground/80 leading-relaxed">
              Monitor and manage your wind turbines with real-time insights
            </p>

            <div className="mt-12 flex flex-col gap-4">
              <FeatureRow icon={<Activity className="size-5" />} label="Real-time monitoring" />
              <FeatureRow icon={<MapPin className="size-5" />} label="Location tracking" />
              <FeatureRow icon={<Settings2 className="size-5" />} label="Remote device control" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
